import { useToast } from '@/context/ToastContext';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

const DOMAIN = import.meta.env.VITE_API_DOMAIN;
let axiosInstance: AxiosInstance | null = null;

const getBaseURL = (port: string, prefixURL: string = ''): string => {
  return `${DOMAIN}:${port}${prefixURL}`;
};

const getAccessToken = (): string | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.warn("⚠️ No 'user' key found in localStorage");
      return null;
    }

    const user = JSON.parse(userStr);
    const token = user?.accessToken;

    if (!token) {
      console.warn("⚠️ accessToken not found inside 'user' object");
    }

    return token || null;
  } catch (e) {
    console.error("Failed to parse 'user' from localStorage", e);
    return null;
  }
};

// Create or get axios instance
const getAxiosInstance = (port: string, prefixURL: string = ''): AxiosInstance => {
  const baseURL = getBaseURL(port, prefixURL);

  // Create new instance only if it doesn't exist or baseURL changed
  if (!axiosInstance) {
    axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request Interceptor - Attach token
    axiosInstance.interceptors.request.use(
      (config) => {
        const token = getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn(
            `⚠️ No token found for request: ${config.method?.toUpperCase()} ${config.url}`,
          );
        }
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      },
    );

    // Response Interceptor
    axiosInstance.interceptors.response.use(
      (response) => {
        if (response?.data?.exp === 'token expired') {
          console.log('Session Expired. Please login again.');
          clearAuthToken();
          window.location.replace('/login');
          return Promise.reject('Token expired');
        }
        return response;
      },
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.log('Session Expired. Please login again.');
          clearAuthToken();
          window.location.replace('/login');
        } else if (error.response?.status === 403) {
          console.log('Access Denied');
        }
        return Promise.reject(error);
      },
    );
  }
  // If baseURL changed, update it
  else if (axiosInstance.defaults.baseURL !== baseURL) {
    axiosInstance.defaults.baseURL = baseURL;
    console.log(`🔄 Updated baseURL to: ${baseURL}`);
  }

  return axiosInstance;
};

export const apiService = {
  get: async <T>(
    endpoint: string,
    port: string,
    prefixURL: string = '',
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const instance = getAxiosInstance(port, prefixURL);
    const response = await instance.get<T>(endpoint, config);
    return response.data;
  },

  post: async <T>(
    endpoint: string,
    data: any,
    port: string,
    prefixURL: string = '',
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const instance = getAxiosInstance(port, prefixURL);
    const response = await instance.post<T>(endpoint, data, config);
    return response.data;
  },

  put: async <T>(
    endpoint: string,
    data: any,
    port: string,
    prefixURL: string = '',
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const instance = getAxiosInstance(port, prefixURL);
    const response = await instance.put<T>(endpoint, data, config);
    return response.data;
  },

  patch: async <T>(
    endpoint: string,
    data: any,
    port: string,
    prefixURL: string = '',
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const instance = getAxiosInstance(port, prefixURL);
    const response = await instance.patch<T>(endpoint, data, config);
    return response.data;
  },

  delete: async <T>(
    endpoint: string,
    port: string,
    prefixURL: string = '',
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const instance = getAxiosInstance(port, prefixURL);
    const response = await instance.delete<T>(endpoint, config);
    return response.data;
  },
  upload: async <T>(
    endpoint: string,
    formData: FormData,
    port: string,
    prefixURL: string = '',
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const instance = getAxiosInstance(port, prefixURL);

    const response = await instance.post<T>(endpoint, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },
};

// Optional: Helper to manually set token (useful after login)
export const setAuthToken = (token: string) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    user.accessToken = token;
    localStorage.setItem('user', JSON.stringify(user));
    console.log('✅ Token manually set in localStorage');
  } catch (e) {
    console.error('Failed to set auth token', e);
  }
};

// Optional: Clear token on logout
export const clearAuthToken = () => {
  localStorage.removeItem('user');
  if (axiosInstance) {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
  console.log('🗑️ Auth token cleared');
};
